'use client'
import { CheckCircle, Close, Error, Info, QuestionMark, Warning } from '@mui/icons-material'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import { JSX, forwardRef, useEffect, useRef, useState } from 'react'
import intl from 'components/i18n/ReactIntlWrapper'
import Spinner from 'components/Spinner'

export interface IDialogMessage {
    id?: string
    title?: string
    message: string | JSX.Element
    type: 'success' | 'error' | 'warning' | 'info' | 'confirm'
    handleClose: (message: IDialogMessage) => void
    loading?: boolean
    confirmButton?: {
        confirmLabel?: string
        confirmClassName?: string
        handleConfirm: (message: IDialogMessage) => void | Promise<void>
    }
    cancelButton?: {
        cancelLabel?: string
        cancelClassName?: string
        handleCancel: (message: IDialogMessage) => void
    }
}

const iconMap = {
    success: { icon: <CheckCircle color='success' fontSize="large" />},
    error: { icon: <Error color='error' fontSize="large" />},
    warning: { icon: <Warning color='warning' fontSize="large" />},
    info: { icon: <Info color='info' fontSize="large" />},
    confirm: { icon: <QuestionMark color='primary' fontSize="large" />},
};

const DefaultDialog = forwardRef<HTMLDivElement, IDialogMessage>((props, ref) => {
    const {
        id,
        title,
        message,
        type,
        handleClose,
        loading,
        confirmButton,
        cancelButton
    } = props;

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const [confirming, setConfirming] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const isLoading = Boolean(loading) || confirming;

    const handleConfirmClick = () => {
        if (!confirmButton?.handleConfirm) return;
        try {
            const result = confirmButton.handleConfirm(props);
            if (result instanceof Promise) {
                setConfirming(true);
                result.finally(() => {
                    if (mountedRef.current) {
                        setConfirming(false);
                    }
                });
            }
        } catch (e) {
            if (mountedRef.current) {
                setConfirming(false);
            }
        }
    };

    // Determinar el estilo del diálogo según el tipo
    const getDialogStyles = () => {
        switch (type) {
            case 'error':
                return { paper: { bgcolor: 'error.main' } };
            case 'success':
                return { paper: { bgcolor: 'success.main' } };
            case 'warning':
                return { paper: { bgcolor: 'warning.main' } };
            default:
                return {};
        }
    };

    return (
        <Dialog
            ref={ref}
            key={id}
            fullScreen={fullScreen}
            open={true}
            maxWidth="sm"
            fullWidth
            onClose={(event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
                handleClose(props);
            }}
            disableEscapeKeyDown={true}
            aria-labelledby="responsive-dialog-title"
            sx={getDialogStyles()}
        >
            <DialogTitle 
                id="responsive-dialog-title" 
                sx={{
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    gap: 2,
                    mx: 0,
                    p: 1.5,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ fontSize: '1.25rem' }}>
                        {title}
                    </Typography>
                </Box>
                {type !== 'error' && (
                    <Close sx={{cursor: 'pointer'}} onClick={()=>handleClose(props)} />
                )}
            </DialogTitle>
            <DialogContent>
                <DialogContentText component="div">
                    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                        <Stack gap={2}>
                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                {iconMap[type].icon}
                            </Box>
                            <Box sx={{ fontSize: '1rem' }}>{message}</Box>
                        </Stack>
                    </Box>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Spinner small={true} />
                    </Box>
                )}
                {cancelButton?.handleCancel && !isLoading && (
                    <Button
                        color='primary'
                        variant="outlined"
                        sx={cancelButton.cancelClassName ? {} : {}}
                        onClick={() => cancelButton.handleCancel(props)}
                    >
                        {cancelButton.cancelLabel || intl.get('cancel.button')}
                    </Button>
                )}
                {confirmButton?.handleConfirm && !isLoading && (
                    <Button
                        variant="contained"
                        color='primary'
                        sx={confirmButton.confirmClassName ? {} : { '&:hover': { opacity: 0.9 } }}
                        onClick={handleConfirmClick}
                        autoFocus={type === 'confirm'}
                    >
                        {confirmButton.confirmLabel || intl.get('accept.button')}
                    </Button>
                )}
                {!confirmButton?.handleConfirm && !cancelButton?.handleCancel && !isLoading && (
                    <Button 
                        variant="contained" 
                        sx={type === 'error' ? { bgcolor: 'red.900' } : {}} 
                        onClick={() => handleClose(props)}
                    >
                        {intl.get('accept.button')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
});

DefaultDialog.displayName = 'DefaultDialog';
export default DefaultDialog;